import axiosNest from '../../api/axiosNest';

/**
 * Reguła podwykonawcy klienta z właścicielem, datą obowiązywania, zmianą
 * zaplanowaną i historią (prace 25.10.2026, S-25.10-06, przełącznik
 * `subcontractorRuleOwnership`; paczka: intake). Endpointy:
 * `apps/api/src/contractors/contractors.controller.ts`, reguły:
 * `subcontractor-rule-schedule.ts` w API.
 */

/** Klucz przełącznika — dane. */
export const FEATURE_RULE_OWNERSHIP = 'subcontractorRuleOwnership';

/** Zmiana zaplanowana na karcie kontrahenta. */
export interface ScheduledRule {
  rule: string | null;
  label: string;
  validFrom: string;
  changeId: number | null;
}

export interface RuleOwnerOption {
  id: number;
  username: string;
}

/** Wpis historii reguły i właściciela. */
export interface RuleHistoryEntry {
  id: number;
  kind: 'rule' | 'owner' | string;
  oldRuleLabel: string;
  newRuleLabel: string;
  oldOwnerName: string | null;
  newOwnerName: string | null;
  source: string;
  fileName: string | null;
  fileLine: number | null;
  reason: string | null;
  notes: string | null;
  validFrom: string | null;
  state: 'applied' | 'scheduled' | 'cancelled' | string;
  cancelledByName: string | null;
  cancelReason: string | null;
  changedByName: string | null;
  changedAt: string;
}

/** Stan wpisu dla człowieka. */
export const RULE_CHANGE_STATE_LABELS: Record<string, string> = {
  applied: 'obowiązuje',
  scheduled: 'zaplanowana',
  cancelled: 'odwołana',
};

export async function fetchRuleOwners(): Promise<RuleOwnerOption[]> {
  const { data } = await axiosNest.get<RuleOwnerOption[]>('/contractors/subcontracting-owners');
  return Array.isArray(data) ? data : [];
}

export async function fetchRuleHistory(contractorId: number): Promise<RuleHistoryEntry[]> {
  const { data } = await axiosNest.get<RuleHistoryEntry[]>(
    `/contractors/${contractorId}/subcontracting-rule-history`,
  );
  return Array.isArray(data) ? data : [];
}

export interface RuleChangeRequest {
  rule?: string | null;
  notes: string;
  reason: string;
  validFrom: string | null;
  ownerId?: number | null;
}

/**
 * Zapis z przełącznikiem: reguła (pomijana, gdy bez zmiany), uwagi, powód,
 * „obowiązuje od” (pusta = od razu) i właściciel (pomijany, gdy bez zmiany).
 */
export async function saveRuleChange(contractorId: number, req: RuleChangeRequest): Promise<unknown> {
  const body: Record<string, unknown> = {
    uwagi: req.notes,
    reason: req.reason,
  };
  if (req.rule !== undefined) body.regula = req.rule;
  if (req.validFrom) body.validFrom = req.validFrom;
  if (req.ownerId !== undefined) body.ownerId = req.ownerId;
  const { data } = await axiosNest.put(`/contractors/${contractorId}/subcontracting`, body);
  return data;
}

export async function cancelScheduledRule(
  contractorId: number,
  changeId: number,
  reason: string,
): Promise<RuleHistoryEntry[]> {
  const { data } = await axiosNest.post<RuleHistoryEntry[]>(
    `/contractors/${contractorId}/subcontracting-changes/${changeId}/cancel`,
    { reason },
  );
  return data;
}

/** Opis wpisu: zmiana reguły albo właściciela. */
export function ruleHistoryChangeText(h: RuleHistoryEntry): string {
  if (h.kind === 'owner') {
    return `właściciel: ${h.oldOwnerName ?? 'nieustalony'} → ${h.newOwnerName ?? 'nieustalony'}`;
  }
  return `${h.oldRuleLabel} → ${h.newRuleLabel}`;
}
